import React, { useCallback, useLayoutEffect, useState } from "react";
import usePrefersReducedMotion from "./usePrefersReducedMotion";

export type SlidingIndicatorKey = string | number | null | undefined;

/**
 * `instant` — geometry applies without a transition (first paint, return from hidden, resize, reduced motion).
 * `animate` — geometry changes slide.
 * `hidden`  — there is no active element; the indicator fades out on the row it last stood on, and
 *             takes no space once that row is gone.
 */
export type SlidingIndicatorState = "instant" | "animate" | "hidden";

export interface ISlidingIndicatorOptions {
  /** Hides the indicator, e.g. while react-beautiful-dnd drags a row out from under it. */
  hidden?: boolean;
  /**
   * Finds the active element by selector instead of by `data-indicator-key` — for rows the caller
   * cannot tag (rc-tree's `.rc-tree-treenode-selected`, a legacy `…--active` class). The active key
   * still drives when to re-measure.
   */
  activeSelector?: string;
}

export const INDICATOR_KEY_ATTRIBUTE: string = "data-indicator-key";
export const INDICATOR_STATE_ATTRIBUTE: string = "data-indicator-state";

const INDICATOR_X: string = "--indicator-x";
const INDICATOR_Y: string = "--indicator-y";
const INDICATOR_W: string = "--indicator-w";
const INDICATOR_H: string = "--indicator-h";
const GEOMETRY_PROPERTIES: string[] = [INDICATOR_X, INDICATOR_Y, INDICATOR_W, INDICATOR_H];

interface IIndicatorGeometry {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface IIndicatorTarget {
  container: HTMLElement | null;
  activeKey: SlidingIndicatorKey;
  hidden: boolean;
  activeSelector: string | undefined;
  prefersReducedMotion: boolean;
}

const EMPTY_TARGET: IIndicatorTarget = {
  container: null,
  activeKey: undefined,
  hidden: false,
  activeSelector: undefined,
  prefersReducedMotion: false
};

const isSameTarget = (a: IIndicatorTarget, b: IIndicatorTarget): boolean =>
  a.container === b.container &&
  a.activeKey === b.activeKey &&
  a.hidden === b.hidden &&
  a.activeSelector === b.activeSelector &&
  a.prefersReducedMotion === b.prefersReducedMotion;

const isSameGeometry = (a: IIndicatorGeometry | null, b: IIndicatorGeometry): boolean =>
  a !== null && a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;

const findActiveElement = (
  container: HTMLElement,
  activeKey: SlidingIndicatorKey,
  activeSelector: string | undefined
): HTMLElement | null => {
  if (activeKey === null || activeKey === undefined) {
    return null;
  }

  if (activeSelector !== undefined) {
    return container.querySelector<HTMLElement>(activeSelector);
  }

  // Compared as attribute text, so a key never has to be escaped into a selector.
  const wantedKey: string = String(activeKey);
  const candidates: NodeListOf<HTMLElement> = container.querySelectorAll<HTMLElement>(`[${INDICATOR_KEY_ATTRIBUTE}]`);

  for (let index = 0; index < candidates.length; index++) {
    if (candidates[index].getAttribute(INDICATOR_KEY_ATTRIBUTE) === wantedKey) {
      return candidates[index];
    }
  }

  return null;
};

/**
 * The active element's layout box, walked up its offsetParent chain to the container. Layout
 * offsets ignore transforms, so a row caught mid-press (a button's `scale`) or a container inside a
 * scaling ancestor (a modal's entrance) still measures at its resting size. `null` when the chain
 * never reaches the container — it is not the rows' containing block, or the element is not laid out.
 */
const measureLayoutBox = (container: HTMLElement, active: HTMLElement): IIndicatorGeometry | null => {
  let x: number = 0;
  let y: number = 0;
  let node: HTMLElement = active;

  while (node !== container) {
    const parent: Element | null = node.offsetParent;

    if (!(parent instanceof HTMLElement)) {
      return null;
    }

    x += node.offsetLeft;
    y += node.offsetTop;

    // An offset is measured from the parent's padding edge and ignores the parent's own scroll; the
    // container's scroll is left in, because the indicator scrolls with the container's content.
    if (parent !== container) {
      x += parent.clientLeft - parent.scrollLeft;
      y += parent.clientTop - parent.scrollTop;
    }

    node = parent;
  }

  return { x, y, width: active.offsetWidth, height: active.offsetHeight };
};

/** The same box from the viewport rects, which keep sub-pixel positions but include transforms. */
const measureRects = (container: HTMLElement, active: HTMLElement): IIndicatorGeometry => {
  const containerRect: DOMRect = container.getBoundingClientRect();
  const activeRect: DOMRect = active.getBoundingClientRect();

  return {
    x: activeRect.left - containerRect.left - container.clientLeft + container.scrollLeft,
    y: activeRect.top - containerRect.top - container.clientTop + container.scrollTop,
    width: activeRect.width,
    height: activeRect.height
  };
};

const TRANSFORM_PROPERTIES: string[] = ["transform", "scale", "translate", "rotate"];

const isTransformed = (element: HTMLElement): boolean => {
  const style: CSSStyleDeclaration = window.getComputedStyle(element);

  return TRANSFORM_PROPERTIES.some((property: string): boolean => {
    const value: string = style.getPropertyValue(property);
    return value !== "" && value !== "none";
  });
};

/**
 * Whether the rects would not show the resting layout: something between the row and the
 * container is transformed (a pressed button, a pane still sliding in), or the container itself is
 * drawn at a scale (a modal scaling in). Offsets are whole pixels, so they are used only then.
 */
const isDisplacedByTransform = (container: HTMLElement, active: HTMLElement): boolean => {
  const drawnWidth: number = container.getBoundingClientRect().width;

  if (container.offsetWidth > 0 && Math.abs(drawnWidth - container.offsetWidth) > 1) {
    return true;
  }

  for (let node: HTMLElement | null = active; node !== null && node !== container; node = node.parentElement) {
    if (isTransformed(node)) {
      return true;
    }
  }

  return false;
};

/** The active element's box in the container's scrolled content coordinates — where an absolutely positioned child lands. */
const measure = (container: HTMLElement, active: HTMLElement): IIndicatorGeometry => {
  if (isDisplacedByTransform(container, active)) {
    return measureLayoutBox(container, active) ?? measureRects(container, active);
  }

  return measureRects(container, active);
};

/**
 * False inside a `display: none` subtree — a closed folder, a pane hidden by CSS — where there is no
 * box to point at. Where the browser cannot tell (no `checkVisibility`), the element counts as rendered.
 */
const isRendered = (element: HTMLElement): boolean =>
  typeof element.checkVisibility !== "function" || element.checkVisibility();

/**
 * Forces a style recalculation. Switching a transition on is only safe once the browser has
 * computed the element WITH its final geometry and WITHOUT the transition; otherwise it animates
 * from wherever it last painted — on the first paint, from the container's top-left corner.
 */
const flushStyles = (element: HTMLElement): void => {
  element.getBoundingClientRect();
};

/** Owns one container at a time: measures, writes the CSS variables and the state, observes resizes. */
class SlidingIndicatorController {
  private target: IIndicatorTarget = EMPTY_TARGET;
  private observer: ResizeObserver | null = null;
  private observedActive: HTMLElement | null = null;
  private geometry: IIndicatorGeometry | null = null;
  /** The row the indicator last stood on — where it keeps fading out while there is no active one. */
  private lastActive: HTMLElement | null = null;

  public sync(next: IIndicatorTarget): void {
    if (isSameTarget(this.target, next)) {
      return;
    }

    if (next.container !== this.target.container) {
      this.detach();
      this.attach(next.container);
    }

    this.target = next;
    this.place(false);
  }

  /** Re-reads the active element's box, e.g. after rows were inserted above it without a resize. */
  public remeasure(): void {
    this.place(false);
  }

  public dispose(): void {
    this.detach();
    this.target = EMPTY_TARGET;
  }

  private readonly handleResize = (): void => {
    // A resize follows the layout rather than the user, so it snaps instead of sliding.
    this.place(true);
  };

  private attach(container: HTMLElement | null): void {
    if (container === null || typeof ResizeObserver === "undefined") {
      return;
    }

    this.observer = new ResizeObserver(this.handleResize);
    this.observer.observe(container);
  }

  private detach(): void {
    this.observer?.disconnect();
    this.observer = null;
    this.observedActive = null;
    this.geometry = null;
    this.lastActive = null;

    const container: HTMLElement | null = this.target.container;

    if (container !== null) {
      container.removeAttribute(INDICATOR_STATE_ATTRIBUTE);
      GEOMETRY_PROPERTIES.forEach((property: string): void => {
        container.style.removeProperty(property);
      });
    }
  }

  private observeActive(active: HTMLElement | null): void {
    if (this.observer === null || active === this.observedActive) {
      return;
    }

    if (this.observedActive !== null) {
      this.observer.unobserve(this.observedActive);
    }

    if (active !== null) {
      this.observer.observe(active);
    }

    this.observedActive = active;
  }

  private place(snap: boolean): void {
    const { container, activeKey, hidden, activeSelector, prefersReducedMotion }: IIndicatorTarget = this.target;

    if (container === null) {
      return;
    }

    const active: HTMLElement | null = hidden ? null : findActiveElement(container, activeKey, activeSelector);

    // An active element that is not rendered stays observed: it reports a resize when it shows again.
    this.observeActive(active);

    const geometry: IIndicatorGeometry | null =
      active !== null && isRendered(active) ? measure(container, active) : null;

    if (geometry === null) {
      this.hide(container);
      return;
    }

    this.lastActive = active;

    const state: string | null = container.getAttribute(INDICATOR_STATE_ATTRIBUTE);
    const isAnimating: boolean = state === "animate";

    // A ResizeObserver also reports on observe(); an unchanged box must not interrupt a slide in progress.
    // A hidden indicator is never "unchanged": it kept the geometry it faded out at, so coming back to
    // that same row must still show it.
    if (state !== "hidden" && isSameGeometry(this.geometry, geometry) && (isAnimating || prefersReducedMotion)) {
      return;
    }

    if (isAnimating && !snap && !prefersReducedMotion) {
      this.write(container, geometry);
      return;
    }

    container.setAttribute(INDICATOR_STATE_ATTRIBUTE, "instant");
    this.write(container, geometry);

    if (prefersReducedMotion) {
      return;
    }

    flushStyles(container);
    container.setAttribute(INDICATOR_STATE_ATTRIBUTE, "animate");
  }

  /**
   * No active element: the indicator fades out on the row it last stood on — re-measured, since rows
   * may have moved under it. Once that row is gone or not rendered, the box goes too: kept at a stale
   * offset inside a scroll container, it would hold the scrollable overflow at the old list's height,
   * and a list filtered down to a few rows would stay scrolled into empty space.
   */
  private hide(container: HTMLElement): void {
    const last: HTMLElement | null = this.lastActive;

    if (last !== null && container.contains(last) && isRendered(last)) {
      this.write(container, measure(container, last));
    } else {
      this.lastActive = null;
      this.geometry = null;
      GEOMETRY_PROPERTIES.forEach((property: string): void => {
        container.style.removeProperty(property);
      });
    }

    container.setAttribute(INDICATOR_STATE_ATTRIBUTE, "hidden");
  }

  private write(container: HTMLElement, geometry: IIndicatorGeometry): void {
    container.style.setProperty(INDICATOR_X, `${geometry.x}px`);
    container.style.setProperty(INDICATOR_Y, `${geometry.y}px`);
    container.style.setProperty(INDICATOR_W, `${geometry.width}px`);
    container.style.setProperty(INDICATOR_H, `${geometry.height}px`);
    this.geometry = geometry;
  }
}

/**
 * Drives one sliding "active item" indicator — rails, panel lists, radio groups, the entity
 * switcher: a single element that slides between rows instead of each row painting its own
 * selected background.
 *
 * The hook only measures and writes. It puts the active element's box, relative to the container,
 * into `--indicator-x/y/w/h` on the container and sets `data-indicator-state`
 * (`instant` | `animate` | `hidden`). The caller renders the indicator itself: a direct child of the
 * container with the shared `kuika-sliding-indicator` class (styles/base/motion.scss) plus its own
 * colour and radius. The container must be the indicator's containing block (`position: relative`)
 * and should be the rows' own parent rather than a fixed-height scroll viewport, so that rows
 * shifting inside it resize it and trigger a re-measure.
 *
 * Rows mark themselves with `data-indicator-key={key}`; `activeSelector` covers rows that cannot.
 * The first placement never slides (no hop in from the top-left), resizes snap, and under
 * reduced motion every change is instant. A transform never shrinks or shifts the box: when the row
 * or anything between it and the container is transformed (a pressed button, a pane sliding in), or
 * the container is drawn at a scale (a modal scaling in), the row is measured by its layout box. An
 * active row that is not rendered (inside a `display: none` subtree) counts as no active row. With no
 * active row the indicator keeps its box on the row it last stood on while that row is rendered, and
 * none once it is gone.
 *
 * @returns `remeasure` — for layout changes the container's size does not reveal.
 */
const useSlidingIndicator = (
  containerRef: React.RefObject<HTMLElement>,
  activeKey: SlidingIndicatorKey,
  options: ISlidingIndicatorOptions = {}
): (() => void) => {
  const prefersReducedMotion: boolean = usePrefersReducedMotion();
  const [controller] = useState<SlidingIndicatorController>(
    (): SlidingIndicatorController => new SlidingIndicatorController()
  );

  // Runs after every render but only compares references and values; it measures only when the
  // container, the key or an option actually changed. That is also how a container swapped out
  // by conditional rendering gets released.
  useLayoutEffect((): void => {
    controller.sync({
      container: containerRef.current,
      activeKey,
      hidden: options.hidden === true,
      activeSelector: options.activeSelector,
      prefersReducedMotion
    });
  });

  useLayoutEffect((): (() => void) => {
    return (): void => {
      controller.dispose();
    };
  }, [controller]);

  return useCallback((): void => {
    controller.remeasure();
  }, [controller]);
};

export default useSlidingIndicator;
